/**
 * "At a glance" (TMMRSL-0049): three figures from the same 24-month example as `PlanExample` —
 * the commission over 24 months, the reseller's margin after it, and the first day a commission
 * becomes payable. When the example has a bounty (TMMAPI-0105, TMMRSL-0051), its total is a fourth
 * row. While there's no example it shows the example's state text, compact.
 */
import { Alert, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { GrantedCard } from '@/api/rateCards';
import { BusyRegion } from '@/components/BusyRegion';
import { DefinitionField, DefinitionList } from '@/components/DefinitionList';
import { moneyIn } from '@/components/moneyIn';
import { describeApiError } from '@/components/useApiErrorHandler';
import type { RemoteState } from '@/components/useRemote';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { exampleTotals, exampleView, firstPayableOn } from './exampleState';
import type { PlanPreviewState } from './usePlanPreview';

export interface PlanSummaryPanelProps {
  state: PlanPreviewState;
  cards: RemoteState<GrantedCard[]>;
  choices: GrantedCard[];
}

export function PlanSummaryPanel({ state, cards, choices }: PlanSummaryPanelProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const dash = t('statements.unavailable');
  const view = exampleView(state, cards, choices);
  const busy = view.kind === 'ready' && view.busy;

  function body() {
    switch (view.kind) {
      case 'cardsError':
        return (
          <Alert severity="error">{describeApiError(view.error, t).message}</Alert>
        );
      case 'failed':
        return <Alert severity="error">{t('commissionPlans.example.failed')}</Alert>;
      case 'cardGone':
        return <Alert severity="warning">{t('commissionPlans.example.cardGone')}</Alert>;
      case 'cardsLoading':
      case 'calculating':
      case 'noCard':
      case 'idle':
      case 'invalid': {
        const key = {
          cardsLoading: 'calculating',
          calculating: 'calculating',
          noCard: 'noCard',
          idle: 'fixFields',
          invalid: 'fixFields',
        }[view.kind];
        return (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t(`commissionPlans.example.${key}`)}
          </Typography>
        );
      }
      case 'ready':
        break;
    }
    const { example } = view;
    const money = (minor: number | null) =>
      minor == null ? dash : moneyIn(minor, example.currency, locale, dash);
    const totals = exampleTotals(example);
    const first = firstPayableOn(example);
    return (
      <DefinitionList>
        <DefinitionField label={t('commissionPlans.summary.commission')}>
          {money(totals.commissionMinor)}
        </DefinitionField>
        {example.hasBounty ? (
          <DefinitionField label={t('commissionPlans.summary.bounty')}>
            {money(totals.bountyMinor)}
          </DefinitionField>
        ) : null}
        <DefinitionField label={t('commissionPlans.summary.marginAfter')}>
          {money(totals.marginAfterMinor)}
        </DefinitionField>
        <DefinitionField label={t('commissionPlans.summary.firstPayable')}>
          {first === null ? dash : formatCivilDate(first, locale)}
        </DefinitionField>
      </DefinitionList>
    );
  }

  return (
    <Paper sx={{ p: 2.5 }}>
      <Typography variant="subtitle1" component="h2" sx={{ mb: 1.5 }}>
        {t('commissionPlans.summary.title')}
      </Typography>
      <BusyRegion busy={busy}>{body()}</BusyRegion>
    </Paper>
  );
}
